/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
/** Custom Hooks */
import useDebounce from "../../app/customHooks/useDebounce";
/** Common Components */
import CardSection from "../../components/ui/card/CardSection";
import CenterModel from "../../components/ui/centerModel/CenterModel";
import FinalMolGrid from "../../components/FinalMolGrid/FinalMolGrid";
/** Common Services & Data Files */
import ToastService from "../../services/toastService/ToastService";
import SwalAlert from "../../services/swalService/SwalService";
import AddEditStatusCategory from "./features/AddEditStatusCategory";
import { StatusCategoriesGridConfig } from "./config/statusCategories.data";
import {
  useDeleteStatusCategoryMutation,
  useGetAllStatusCategoriesWithPaginationMutation,
} from "../../app/services/statusCategoryAPI";

const StatusCategories = () => {
  const molGridRef = useRef();
  const { confirm } = SwalAlert();

  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 300);
  const [showModal, setShowModal] = useState(false);
  const [listData, setListData] = useState([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [initData, setInitData] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  const [
    getAllStatusCategories,
    {
      isLoading: isGetAllStatusCategoriesLoading,
      isSuccess: isGetAllStatusCategoriesSuccess,
      data: getAllStatusCategoriesData,
    },
  ] = useGetAllStatusCategoriesWithPaginationMutation();

  const [
    deleteStatusCategory,
    {
      isSuccess: isDeleteSuccess,
      isLoading: isDeleteLoading,
      data: isDeleteData,
    },
  ] = useDeleteStatusCategoryMutation();

  useEffect(() => {
    if (isGetAllStatusCategoriesSuccess && getAllStatusCategoriesData) {
      setListData(getAllStatusCategoriesData.dataSource);
      setTotalRowCount(getAllStatusCategoriesData.totalRecord);
    }
  }, [isGetAllStatusCategoriesSuccess, getAllStatusCategoriesData]);

  useEffect(() => {
    if (isDeleteSuccess && isDeleteData) {
      if (
        isDeleteData.errorMessage?.toLowerCase().includes("cannot be deleted") ||
        isDeleteData.errorMessage?.toLowerCase().includes("in use") ||
        isDeleteData.errorMessage?.toLowerCase().includes("no record") ||
        isDeleteData.keyValue === 0
      ) {
        ToastService.warning(isDeleteData.errorMessage);
      } else {
        ToastService.success(isDeleteData.errorMessage);
        const currentPageObject = molGridRef.current.getCurrentPageObject();
        getStatusCategoryList(currentPageObject);
      }
    }
  }, [isDeleteSuccess, isDeleteData]);

  useEffect(() => {
    if (molGridRef.current) {
      const currentPageObject = molGridRef.current.getCurrentPageObject();
      getStatusCategoryList(currentPageObject);
    }
  }, [debouncedSearch]);

  const handleOnSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const getStatusCategoryList = (pageObject) => {
    const request = {
      pagination: {
        pageNumber: pageObject.pageNumber,
        pageSize: pageObject.pageSize,
      },
      filters: { searchText: debouncedSearch },
    };
    getAllStatusCategories(request);
  };

  const handlePageChange = (pageObject) => {
    setPagination((prev) => ({
      ...prev,
      pageNumber: pageObject.pageNumber,
      pageSize: pageObject.pageSize,
    }));
    getStatusCategoryList(pageObject);
  };

  const onEditAction = (data) => {
    setInitData(data);
    setShowModal(true);
    setIsEdit(true);
  };

  const handleSuccess = () => {
    setShowModal(false);
    setListData(null);
    const currentPageObject = molGridRef.current.getDefulatPageObject();
    getStatusCategoryList(currentPageObject);
  };

  const handleStatusCategoryModal = () => {
    setInitData(null);
    setShowModal(!showModal);
    setIsEdit(false);
    if (molGridRef.current) {
      const currentPageObject = molGridRef.current.getCurrentPageObject();
      getStatusCategoryList(currentPageObject);
    }
  };

  const handleDeleteClick = (data) => {
    if (data?.isSystemDefault) {
      ToastService.warning("System default status categories cannot be deleted");
      return;
    }

    confirm(
      "Delete?",
      "Are you sure you want to delete this Status Category?",
      "Delete",
      "Cancel"
    ).then((confirmed) => {
      if (confirmed) {
        deleteStatusCategory(data.statusCategoryId);
      }
    });
  };

  const actionHandler = {
    DELETE: handleDeleteClick,
    EDIT: onEditAction,
  };

  return (
    <>
      <div className="source-control-list center-top-space">
        <CardSection
          isContentLoading={isGetAllStatusCategoriesLoading}
          cardTitle={"Status Category List"}
          rightButton={true}
          titleButtonClick={handleStatusCategoryModal}
          buttonClassName="theme-btn"
          textWithIcon={true}
          iconName="material-symbols:add-rounded"
          fontIcon={true}
          buttonText="Add Status Category"
          searchInputName="Search by Status Category"
          searchInput={true}
          handleChange={handleOnSearchChange}
        >
          <div className="table-grid">
            <FinalMolGrid
              ref={molGridRef}
              configuration={StatusCategoriesGridConfig}
              dataSource={listData}
              allowPagination={true}
              pagination={{
                totalCount: totalRowCount,
                pageSize: pagination.pageSize,
                currentPage: pagination.pageNumber,
              }}
              isLoading={isGetAllStatusCategoriesLoading || isDeleteLoading}
              onActionChange={actionHandler}
              onPageChange={handlePageChange}
            />
          </div>
        </CardSection>
      </div>
      <div>
        {showModal && (
          <CenterModel
            showModal={showModal}
            handleToggleModal={handleStatusCategoryModal}
            modalTitle={isEdit ? "Update Status Category" : "Add Status Category"}
            modelSizeClass="w-40"
          >
            <div className="row">
              <AddEditStatusCategory
                onClose={handleStatusCategoryModal}
                statusCategoryData={initData}
                isEdit={isEdit}
                onSuccess={handleSuccess}
              />
            </div>
          </CenterModel>
        )}
      </div>
    </>
  );
};

export default StatusCategories;
